/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 *
 */

import React from 'react';
import {
  Box, ButtonGroup, Grid, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useContractForm } from './useContractForm';
import ContractFormSkeleton from './ContractFormSkeleton';
import { ContractSelection } from './ChildComponents/ContractSelection';

/**
 * ContractForm Component
 * @returns {JSX.Element}
 * @constructor
 */
export function ContractForm() {
  const { t } = useTranslation('translations');
  const {
    isLoading,
    termSelection,
    contractSelection,
    getAvailableTerms,
    handleTermSelection,
    handleContractSelection,
    isPartOfTheSelectedContract,
    mergedContractsWithPromotions,
  } = useContractForm();

  /**
   * Render term selection buttons.
   * @returns {JSX.Element} - Term selection buttons.
   */
  const termSelectionRenderer = () => (
    <>
      <Typography mb={1} variant="h6">{t('chooseTermLength')}</Typography>
      <ButtonGroup variant="text" aria-label="text button group">
        {getAvailableTerms()
          .map((termValue) => (
            <Button
              key={termValue}
              variant={termSelection === termValue ? 'contained' : 'outlined'}
              onClick={() => handleTermSelection(termValue)}
            >
              {termValue}
              {' '}
              {termValue === 1 ? t('months')
                .slice(0, -1) : t('months')}
            </Button>
          ))}
      </ButtonGroup>
    </>
  );

  /**
   * Render form elements.
   * @returns {JSX.Element} - Form elements.
   */
  const renderFormElements = () => {
    if (isLoading) {
      return <ContractFormSkeleton />;
    }

    return (
      <>
        <Box sx={{
          mb: 4,
          textAlign: 'center',
        }}
        >
          {termSelectionRenderer()}
        </Box>
        <Grid
          sx={{
            justifyContent: 'center',
          }}
          container
        >
          {mergedContractsWithPromotions.map((bundle) => (
            <ContractSelection
              key={`selection-${bundle.id}`}
              contract={bundle}
              selectedTerm={termSelection}
              setAsSelectedContract={handleContractSelection}
              isSelected={isPartOfTheSelectedContract(bundle, contractSelection)}
            />
          ))}
        </Grid>
      </>
    );
  };

  return (
    <>
      <Typography mt={4} textAlign="center" variant="h2">
        2.
        {' '}
        {t('stepTwoHeader')}
      </Typography>
      {renderFormElements()}
    </>
  );
}
